import { ReactNode } from "react";
import { cn } from "../../lib/utils";

/**
 * The two drawings of a bar of tab chips (`DiscoveryTabBar`,
 * `PracticeFeedRail`, the desktop room Resources tab). Mobile insets the row
 * from the screen edge and uses 40px thumb targets. Desktop sits flush with the feed column it heads and
 * uses the Figma desktop `.TabChip`: 32px, 13px medium, 16px icons whose 1.15px
 * stroke on a 16px grid is 1.75 on Tabler's 24px grid.
 */
export const TAB_BAR_LAYOUTS = {
  mobile: {
    rowStart: "pl-5",
    rowEnd: "pr-5",
    chip: null,
    chipHeight: 40,
    iconStroke: 1.5,
  },
  desktop: {
    rowStart: null,
    rowEnd: null,
    chip: "h-8 px-3 text-footnote font-medium tracking-ds",
    chipHeight: 32,
    iconStroke: 1.75,
  },
} as const;

export type TabBarLayout = keyof typeof TAB_BAR_LAYOUTS;

export function TabButton({
  children,
  icon,
  activeIcon,
  active = false,
  onClick,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      // A filter chip IS a toggle button, so this is first the correct
      // announcement — and it doubles as the one selector a scrolling row can
      // use to find the pressed chip without a ref per chip (`PracticeFeedRail`).
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex h-10 shrink-0 items-center justify-center gap-1 overflow-hidden rounded-lg px-4 py-1 text-sm font-semibold tracking-tight whitespace-nowrap",
        active
          ? "bg-ds-primary text-ds-on-primary"
          : "bg-ds-slate-50 text-ds-mint-950",
        className
      )}
    >
      {active && activeIcon !== undefined ? activeIcon : icon}
      {children}
    </button>
  );
}

/** The new-content dot a chip carries before its icon (`.TabChip` Show Dot). */
export function NewContentDot(): React.JSX.Element {
  return (
    <span className="relative flex size-1.5 shrink-0">
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-ds-strawberry-400 opacity-75" />
      <span className="relative inline-flex size-full rounded-full bg-ds-strawberry-500" />
    </span>
  );
}
